// apps/office/src/pages/Labour.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { useAuth } from '../contexts/AuthContext';
import pb from '../lib/pocketbaseClient';
import LabourModal from '../components/LabourModal';
import LabourDetailModal from '../components/LabourDetailModal';
import LabourSalaryModal from '../components/LabourSalaryModal';
import LabourAttendanceModal from '../components/LabourAttendanceModal';
import { LabourListPDF, LabourCardsPDF } from '../components/LabourPDF';
import {
  Plus, Search, Users, Eye, Edit2, Trash2, DollarSign,
  Download, Calendar, CheckSquare, Square, Trash, LogIn, LogOut,
  CheckCircle, XCircle, AlertCircle, Check
} from 'lucide-react';
import toast from 'react-hot-toast';

export default function Labour() {
  const { user, role } = useAuth();
  const [labours, setLabours] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterVendor, setFilterVendor] = useState('All');
  const [filterSite, setFilterSite] = useState('All');
  const [filterSkill, setFilterSkill] = useState('All');
  const [showModal, setShowModal] = useState(false);
  const [editingLabour, setEditingLabour] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedLabour, setSelectedLabour] = useState(null);
  const [salaryModalOpen, setSalaryModalOpen] = useState(false);
  const [salaryLabour, setSalaryLabour] = useState(null);
  const [attendanceModalOpen, setAttendanceModalOpen] = useState(false);
  const [attendanceLabour, setAttendanceLabour] = useState(null);
  const [activeTab, setActiveTab] = useState('list');

  // Attendance state
  const [todayAttendance, setTodayAttendance] = useState({});
  const [updating, setUpdating] = useState(false);

  // Bulk selection
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectAll, setSelectAll] = useState(false);

  const isMounted = useRef(true);

  // Fetch data
  useEffect(() => {
    isMounted.current = true;
    fetchData();
    return () => { isMounted.current = false; };
  }, []);

  const fetchData = async () => {
    try {
      const [laboursRes, vendorsRes, sitesRes] = await Promise.all([
        pb.collection('labour').getFullList({
          sort: 'name',
          expand: 'vendor,site,created_by',
        }),
        pb.collection('vendors').getFullList({ sort: 'name' }),
        pb.collection('accounts_projects').getFullList({ sort: 'name' }),
      ]);
      if (isMounted.current) {
        setLabours(laboursRes);
        setVendors(vendorsRes);
        setSites(sitesRes);
        await fetchTodayAttendance(laboursRes);
      }
    } catch (err) {
      console.error('Error fetching data:', err);
    } finally {
      if (isMounted.current) setLoading(false);
    }
  };

  const fetchTodayAttendance = async (laboursList) => {
    const today = new Date().toISOString().split('T')[0];
    const map = {};
    for (const lab of laboursList) {
      try {
        const records = await pb.collection('labour_attendance').getFullList({
          filter: `labour="${lab.id}" && date="${today}"`,
        });
        map[lab.id] = records.length > 0 ? records[0] : null;
      } catch {
        map[lab.id] = null;
      }
    }
    setTodayAttendance(map);
  };

  const handleCheckIn = async (labourId) => {
    setUpdating(true);
    try {
      const now = new Date().toISOString();
      const data = {
        labour: labourId,
        date: new Date().toISOString().split('T')[0],
        check_in: now,
        status: 'present',
      };
      const record = await pb.collection('labour_attendance').create(data);
      setTodayAttendance(prev => ({ ...prev, [labourId]: record }));
      toast.success('Checked in');
    } catch (err) {
      console.error(err);
      toast.error('Check-in failed');
    } finally {
      setUpdating(false);
    }
  };

  const handleCheckOut = async (labourId, attendanceId) => {
    setUpdating(true);
    try {
      const now = new Date();
      const nowISO = now.toISOString();
      const hour = now.getHours();
      const isBefore2PM = hour < 14;
      const status = isBefore2PM ? 'halfday' : 'present';
      const updated = await pb.collection('labour_attendance').update(attendanceId, {
        check_out: nowISO,
        status: status,
      });
      setTodayAttendance(prev => ({ ...prev, [labourId]: updated }));
      toast.success(`Checked out${isBefore2PM ? ' (before 2 PM ? half day)' : ''}`);
    } catch (err) {
      console.error(err);
      toast.error('Check-out failed');
    } finally {
      setUpdating(false);
    }
  };

  const handleStatusChange = async (labourId, attendanceId, newStatus) => {
    setUpdating(true);
    try {
      let updated;
      if (attendanceId) {
        updated = await pb.collection('labour_attendance').update(attendanceId, { status: newStatus });
      } else {
        updated = await pb.collection('labour_attendance').create({
          labour: labourId,
          date: new Date().toISOString().split('T')[0],
          status: newStatus,
        });
      }
      setTodayAttendance(prev => ({ ...prev, [labourId]: updated }));
      toast.success(`Status set to ${newStatus}`);
    } catch (err) {
      console.error(err);
      toast.error('Status update failed');
    } finally {
      setUpdating(false);
    }
  };

  // --- CRUD --------------------------------------------------------------
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this labour?')) return;
    try {
      await pb.collection('labour').delete(id);
      toast.success('Labour deleted successfully');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete: ' + err.message);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedItems.length === 0) {
      toast.error('Please select at least one labour');
      return;
    }
    if (!window.confirm(`Delete ${selectedItems.length} selected labour(s)?`)) return;
    try {
      for (const id of selectedItems) {
        await pb.collection('labour').delete(id);
      }
      toast.success(`${selectedItems.length} labour(s) deleted`);
      setSelectedItems([]);
      setSelectAll(false);
      fetchData();
    } catch (err) {
      toast.error('Failed to delete some items');
    }
  };

  const handleSaveLabour = () => {
    setShowModal(false);
    setEditingLabour(null);
    fetchData();
  };

  const openDetailModal = (lab) => {
    setSelectedLabour(lab);
    setDetailModalOpen(true);
  };

  const closeDetailModal = () => {
    setDetailModalOpen(false);
    setSelectedLabour(null);
  };

  const handleEditFromDetail = (lab) => {
    setDetailModalOpen(false);
    setEditingLabour(lab);
    setShowModal(true);
  };

  const openSalaryModal = (lab) => {
    setSalaryLabour(lab);
    setSalaryModalOpen(true);
  };

  const openAttendanceModal = (lab) => {
    setAttendanceLabour(lab);
    setAttendanceModalOpen(true);
  };

  // --- Selection ----------------------------------------------------------
  const toggleSelectItem = (id) => {
    setSelectedItems(prev =>
      prev.includes(id) ? prev.filter(item => item !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectAll) {
      setSelectedItems([]);
    } else {
      const ids = filteredLabours.map(l => l.id);
      setSelectedItems(ids);
    }
    setSelectAll(!selectAll);
  };

  // --- Filters ------------------------------------------------------------
  const filteredLabours = labours.filter(l => {
    const matchVendor = filterVendor === 'All' || l.vendor === filterVendor;
    const matchSite = filterSite === 'All' || l.site === filterSite;
    const matchSkill = filterSkill === 'All' || l.skill === filterSkill;
    const matchSearch = l.name?.toLowerCase().includes(search.toLowerCase()) ||
                        l.aadhar?.includes(search);
    return matchVendor && matchSite && matchSkill && matchSearch;
  });

  const canEdit = (lab) => {
    if (role === 'admin') return true;
    if (['site_engineer','site_manager','site_supervisor'].includes(role)) {
      return lab.created_by === user?.id;
    }
    return false;
  };

  // --- PDF Downloads ------------------------------------------------------
  const downloadListPDF = async () => {
    if (filterVendor === 'All') {
      alert('Please select a vendor to generate the list.');
      return;
    }
    const vendor = vendors.find(v => v.id === filterVendor);
    if (!vendor) return;
    const laboursForVendor = labours.filter(l => l.vendor === filterVendor);
    // PDF generation logic
  };

  const downloadCardsPDF = async () => {
    if (filterVendor === 'All') {
      alert('Please select a vendor to generate cards.');
      return;
    }
    const vendor = vendors.find(v => v.id === filterVendor);
    if (!vendor) return;
    const laboursForVendor = labours.filter(l => l.vendor === filterVendor);
    // PDF generation logic
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="w-10 h-10 border-4 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-slate-500 font-medium">Loading labours...</p>
        </div>
      </div>
    );
  }

  const renderAttendanceStatus = (att) => {
    if (!att) {
      return (
        <Badge variant="outline" className="text-red-600 border-red-300 flex items-center gap-1">
          <XCircle className="w-3 h-3" />
          <span>Absent</span>
        </Badge>
      );
    }
    if (att.status === 'present') {
      return (
        <Badge className="bg-emerald-100 text-emerald-700 border-0 flex items-center gap-1">
          <CheckCircle className="w-3 h-3" />
          <span>Present</span>
        </Badge>
      );
    }
    if (att.status === 'halfday') {
      return (
        <Badge className="bg-yellow-100 text-yellow-700 border-0 flex items-center gap-1">
          <AlertCircle className="w-3 h-3" />
          <span>Half Day</span>
        </Badge>
      );
    }
    return <Badge variant="outline">Unknown</Badge>;
  };

  return (
    <div className="space-y-6 p-4 md:p-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
            <Users className="w-6 h-6 text-amber-500" />
            <span>Labour Management</span>
            <Badge className="bg-amber-100 text-amber-700 ml-2">{filteredLabours.length}</Badge>
          </h1>
          <p className="text-sm text-slate-500">Manage labourers, attendance, and salary</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {selectedItems.length > 0 && (
            <Button
              variant="destructive"
              onClick={handleBulkDelete}
              className="bg-red-500 hover:bg-red-600"
            >
              <Trash className="w-4 h-4 mr-2" />
              Delete Selected ({selectedItems.length})
            </Button>
          )}
          <Button
            variant="outline"
            onClick={downloadListPDF}
            disabled={filterVendor === 'All'}
            className="border-blue-500 text-blue-600 hover:bg-blue-50"
          >
            <Download className="w-4 h-4 mr-2" />
            List
          </Button>
          <Button
            variant="outline"
            onClick={downloadCardsPDF}
            disabled={filterVendor === 'All'}
            className="border-purple-500 text-purple-600 hover:bg-purple-50"
          >
            <Download className="w-4 h-4 mr-2" />
            Cards
          </Button>
          {['admin', 'site_engineer', 'site_manager', 'site_supervisor'].includes(role) && (
            <Button
              className="bg-gradient-to-r from-amber-500 to-orange-500 hover:opacity-90 text-white shadow-md"
              onClick={() => { setEditingLabour(null); setShowModal(true); }}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Labour
            </Button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="list">Labour List</TabsTrigger>
          <TabsTrigger value="salary">Salary Sheet</TabsTrigger>
        </TabsList>

        {/* Labour List Tab */}
        <TabsContent value="list">
          {/* Filters */}
          <div className="flex flex-wrap items-center gap-4 bg-white p-4 rounded-xl shadow-sm border border-slate-100">
            <div className="flex-1 min-w-[200px]">
              <Input
                placeholder="Search name or Aadhar..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
              />
            </div>
            <select
              value={filterVendor}
              onChange={(e) => setFilterVendor(e.target.value)}
              className="px-3 py-2 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-amber-400"
            >
              <option value="All">All Vendors</option>
              {vendors.map(v => (
                <option key={v.id} value={v.id}>{v.name}</option>
              ))}
            </select>
            <select
              value={filterSite}
              onChange={(e) => setFilterSite(e.target.value)}
              className="px-3 py-2 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-amber-400"
            >
              <option value="All">All Sites</option>
              {sites.map(s => (
                <option key={s.id} value={s.id}>{s.name}</option>
              ))}
            </select>
            <select
              value={filterSkill}
              onChange={(e) => setFilterSkill(e.target.value)}
              className="px-3 py-2 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-amber-400"
            >
              <option value="All">All Skills</option>
              <option value="skilled">Skilled</option>
              <option value="unskilled">Unskilled</option>
            </select>
            <Button variant="outline" onClick={() => { setSearch(''); setFilterVendor('All'); setFilterSite('All'); setFilterSkill('All'); }}>
              Clear
            </Button>
          </div>

          {/* Table */}
          <Card>
            <CardContent className="p-0 overflow-x-auto">
              <table className="w-full text-left text-sm">
                <thead className="bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200">
                  <tr>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">
                      <button onClick={toggleSelectAll} className="hover:text-amber-600">
                        {selectAll ? <CheckSquare className="w-4 h-4" /> : <Square className="w-4 h-4" />}
                      </button>
                    </th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Name</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Vendor</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Site</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Skill</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Aadhar</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Status</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Today</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase text-right">Salary</th>
                    <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredLabours.length === 0 ? (
                    <tr>
                      <td colSpan="10" className="px-4 py-8 text-center text-slate-500">
                        No labours found.
                      </td>
                    </tr>
                  ) : (
                    filteredLabours.map((lab) => {
                      const vendor = vendors.find(v => v.id === lab.vendor);
                      const att = todayAttendance[lab.id];
                      const isSelected = selectedItems.includes(lab.id);

                      return (
                        <tr key={lab.id} className="hover:bg-slate-50/50 transition">
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => toggleSelectItem(lab.id)}
                              className="hover:text-amber-600"
                            >
                              {isSelected ? <CheckSquare className="w-4 h-4 text-amber-600" /> : <Square className="w-4 h-4" />}
                            </button>
                          </td>
                          <td className="px-4 py-3">
                            <button
                              onClick={() => openDetailModal(lab)}
                              className="font-medium text-blue-600 hover:underline hover:text-amber-600 flex items-center gap-2"
                            >
                              {lab.photo && (
                                <img
                                  src={pb.files.getUrl(lab, lab.photo)}
                                  alt={lab.name}
                                  className="w-6 h-6 rounded-full object-cover border"
                                />
                              )}
                              {lab.name}
                            </button>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              {vendor?.photo && (
                                <img
                                  src={pb.files.getUrl(vendor, vendor.photo)}
                                  alt={vendor.name}
                                  className="w-5 h-5 rounded-full object-cover border"
                                />
                              )}
                              <span>{vendor?.name || 'N/A'}</span>
                            </div>
                          </td>
                          <td className="px-4 py-3">{lab.expand?.site?.name || 'N/A'}</td>
                          <td className="px-4 py-3">
                            <Badge variant={lab.skill === 'skilled' ? 'success' : 'secondary'} className={lab.skill === 'skilled' ? 'bg-emerald-50' : 'bg-slate-100'}>
                              {lab.skill}
                            </Badge>
                          </td>
                          <td className="px-4 py-3">
                            {lab.aadhar ? (
                              <button
                                onClick={() => {
                                  if (lab.aadharPhoto) {
                                    window.open(pb.files.getUrl(lab, lab.aadharPhoto), '_blank');
                                  }
                                }}
                                className="text-blue-600 hover:text-blue-800 underline"
                              >
                                {lab.aadhar}
                              </button>
                            ) : 'N/A'}
                          </td>
                          <td className="px-4 py-3">
                            <Badge variant={lab.status === 'active' ? 'success' : 'secondary'}>
                              {lab.status}
                            </Badge>
                          </td>
                          <td className="px-4 py-3">
                            {renderAttendanceStatus(att)}
                          </td>
                          <td className="px-4 py-3 text-right font-medium">
                            {lab.dailyRate ? `Rs.${lab.dailyRate}/day` : 'N/A'}
                          </td>
                          <td className="px-4 py-3 text-center">
                            <div className="flex items-center justify-center gap-1 flex-wrap">
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-blue-600 hover:bg-blue-50 p-1.5 h-auto"
                                onClick={() => openDetailModal(lab)}
                                title="View Details"
                              >
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-amber-600 hover:bg-amber-50 p-1.5 h-auto"
                                onClick={() => { setEditingLabour(lab); setShowModal(true); }}
                                title="Edit"
                              >
                                <Edit2 className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-green-600 hover:bg-green-50 p-1.5 h-auto"
                                onClick={() => openSalaryModal(lab)}
                                title="Salary"
                              >
                                <DollarSign className="w-4 h-4" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="text-purple-600 hover:bg-purple-50 p-1.5 h-auto"
                                onClick={() => openAttendanceModal(lab)}
                                title="Attendance History"
                              >
                                <Calendar className="w-4 h-4" />
                              </Button>
                              {canEdit(lab) && (
                                <>
                                  {(() => {
                                    const att = todayAttendance[lab.id];
                                    if (!att) {
                                      return (
                                        <Button
                                          size="sm"
                                          className="bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-1"
                                          onClick={() => handleCheckIn(lab.id)}
                                          disabled={updating}
                                        >
                                          <LogIn className="w-3 h-3" />
                                          Check-In
                                        </Button>
                                      );
                                    }
                                    if (att.status === 'present' && !att.check_out) {
                                      return (
                                        <Button
                                          size="sm"
                                          className="bg-amber-600 hover:bg-amber-700 text-white flex items-center gap-1"
                                          onClick={() => handleCheckOut(lab.id, att.id)}
                                          disabled={updating}
                                        >
                                          <LogOut className="w-3 h-3" />
                                          Check-Out
                                        </Button>
                                      );
                                    }
                                    return (
                                      <Badge variant="outline" className="text-emerald-600 border-emerald-300 flex items-center gap-1">
                                        <Check className="w-3 h-3" />
                                        Done
                                      </Badge>
                                    );
                                  })()}
                                </>
                              )}
                              {role === 'admin' && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-red-600 hover:bg-red-50 p-1.5 h-auto"
                                  onClick={() => handleDelete(lab.id)}
                                  title="Delete"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Salary Sheet Tab */}
        <TabsContent value="salary">
          <SalarySheet
            labours={filteredLabours}
            vendors={vendors}
            sites={sites}
            role={role}
          />
        </TabsContent>
      </Tabs>

      {/* Modals */}
      <LabourModal
        open={showModal}
        onClose={() => { setShowModal(false); setEditingLabour(null); }}
        labour={editingLabour}
        vendors={vendors}
        sites={sites}
        onSave={handleSaveLabour}
      />

      <LabourDetailModal
        labour={selectedLabour}
        open={detailModalOpen}
        onClose={closeDetailModal}
        onEdit={handleEditFromDetail}
        vendors={vendors}
        sites={sites}
      />

      <LabourSalaryModal
        open={salaryModalOpen}
        onClose={() => { setSalaryModalOpen(false); setSalaryLabour(null); }}
        labour={salaryLabour}
        vendors={vendors}
        sites={sites}
        onSave={() => { fetchData(); }}
      />

      <LabourAttendanceModal
        labour={attendanceLabour}
        open={attendanceModalOpen}
        onClose={() => { setAttendanceModalOpen(false); setAttendanceLabour(null); }}
        onUpdate={() => { fetchData(); }}
      />
    </div>
  );
}

// --- Salary Sheet Component ----------------------------------------------
function SalarySheet({ labours, vendors, sites, role }) {
  const [selectedMonth, setSelectedMonth] = useState(new Date().toISOString().slice(0, 7));
  const [attendanceData, setAttendanceData] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAttendance();
  }, [selectedMonth, labours]);

  const fetchAttendance = async () => {
    setLoading(true);
    try {
      const monthStart = `${selectedMonth}-01`;
      const monthEnd = `${selectedMonth}-${new Date(parseInt(selectedMonth.split('-')[1]), parseInt(selectedMonth.split('-')[0]), 0).getDate()}`;

      const attendance = {};
      for (const lab of labours) {
        const records = await pb.collection('labour_attendance').getFullList({
          filter: `labour="${lab.id}" && date >= "${monthStart}" && date <= "${monthEnd}"`,
        });
        attendance[lab.id] = records;
      }
      setAttendanceData(attendance);
    } catch (error) {
      console.error('Error fetching attendance:', error);
      toast.error('Failed to load attendance data');
    } finally {
      setLoading(false);
    }
  };

  const getAttendanceStats = (labId) => {
    const records = attendanceData[labId] || [];
    const present = records.filter(r => r.status === 'present').length;
    const absent = records.filter(r => r.status === 'absent').length;
    const halfDay = records.filter(r => r.status === 'halfday').length;
    return { present, absent, halfDay, total: records.length };
  };

  const calculateSalary = (lab) => {
    const stats = getAttendanceStats(lab.id);
    const dailyRate = lab.dailyRate || 0;
    const totalDays = stats.present + (stats.halfDay * 0.5);
    return totalDays * dailyRate;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-10 h-10 border-4 border-amber-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-4 bg-white p-4 rounded-xl shadow-sm border border-slate-100">
        <div className="flex items-center gap-4">
          <Calendar className="w-5 h-5 text-slate-400" />
          <input
            type="month"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="px-3 py-2 border rounded-lg text-sm bg-white focus:ring-2 focus:ring-amber-400"
          />
        </div>
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-emerald-500" />
            <span>Present</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-yellow-500" />
            <span>Half Day</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-red-500" />
            <span>Absent</span>
          </div>
          <Button variant="outline" size="sm" onClick={fetchAttendance} className="ml-auto">
            Refresh
          </Button>
        </div>
      </div>

      <Card>
        <CardContent className="p-0 overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead className="bg-gradient-to-r from-slate-50 to-slate-100 border-b border-slate-200">
              <tr>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Name</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Vendor</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase">Daily Rate</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase text-center">Present</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase text-center">Half Day</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase text-center">Absent</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase text-right">Total Days</th>
                <th className="px-4 py-3 text-xs font-semibold text-slate-600 uppercase text-right">Salary (Rs.)</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {labours.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center text-slate-500">
                    No labours found.
                  </td>
                </tr>
              ) : (
                labours.map((lab) => {
                  const stats = getAttendanceStats(lab.id);
                  const salary = calculateSalary(lab);
                  const vendor = vendors.find(v => v.id === lab.vendor);
                  return (
                    <tr key={lab.id} className="hover:bg-slate-50/50 transition">
                      <td className="px-4 py-3 font-medium">{lab.name}</td>
                      <td className="px-4 py-3">{vendor?.name || 'N/A'}</td>
                      <td className="px-4 py-3">Rs.{lab.dailyRate || 0}</td>
                      <td className="px-4 py-3 text-center font-medium text-emerald-600">{stats.present}</td>
                      <td className="px-4 py-3 text-center font-medium text-yellow-600">{stats.halfDay}</td>
                      <td className="px-4 py-3 text-center font-medium text-red-600">{stats.absent}</td>
                      <td className="px-4 py-3 text-right font-medium">{(stats.present + stats.halfDay * 0.5).toFixed(1)}</td>
                      <td className="px-4 py-3 text-right font-bold text-emerald-600">Rs.{salary.toFixed(2)}</td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
    </div>
  );
}